<template>
  <div>
    <b-card>
      <breadcrumbs
        :breadcrumb-items="breadcrumbItems"
      />
      <PartnerToolbar
        v-if="Object.keys(partner).length !== 0"
        :partner="partner"
      />
    </b-card>
    <StatsNavigation />
    <b-card class="card-statistics">
      <div class="card-statistics-tools mb-2">
        <div class="head-card">
          <div class="head-card-bottom">
            <div class="head-card-top mb-2">
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                :variant="$route.name === 'dashboard-partner-stats-dsp' ? 'flat-primary' : 'flat-secondary'"
                :to="{ name: 'dashboard-partner-stats-dsp', params: { id: $route.params.id } }"
                pill
              >
                {{ $t('Overview') }}
              </b-button>
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                :variant="$route.name === 'dashboard-partner-stats-dsp-partners' ? 'flat-primary' : 'flat-secondary'"
                :to="{ name: 'dashboard-partner-stats-dsp-partners', params: { id: $route.params.id } }"
                pill
              >
                {{ $t('DSP Partners') }}
              </b-button>
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                :variant="$route.name === 'dashboard-partner-stats-geo' ? 'flat-primary' : 'flat-secondary'"
                :to="{ name: 'dashboard-partner-stats-geo', params: { id: $route.params.id } }"
                pill
              >
                {{ $t('Geo') }}
              </b-button>
              <b-button
                v-ripple.400="'rgba(113, 102, 240, 0.15)'"
                :variant="$route.name === 'dashboard-partner-stats-device' ? 'flat-primary' : 'flat-secondary'"
                :to="{ name: 'dashboard-partner-stats-device', params: { id: $route.params.id } }"
                pill
              >
                {{ $t('Device') }}
              </b-button>
            </div>
          </div>
          <div class="head-card-top">
            <h4>{{ $t('DSP Partner Details') }} - {{ dspPartnerName }}</h4>
          </div>
          <div class="mt-2">
            <StatsDateRangeFilter
              v-model="rangePicker"
              :is-loading="loading"
              @range-change="toggleRange"
              @refresh="refreshData"
            />
          </div>
        </div>
      </div>
      <div
        v-if="loading"
        class="d-flex justify-content-center mb-1"
      >
        <b-spinner label="Loading..." />
      </div>
      <div v-else-if="dspPartnerStats.length > 0">
        <b-table
          small
          :items="dspPartnerStats"
          :fields="fields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :sort-direction="sortDirection"
          responsive
          striped
          foot-clone
        >
          <template #head(date)>
            {{ $t('Date') }}
          </template>

          <template #head(impressions)>
            {{ $t('Impressions') }}
          </template>

          <template #head(clicks)>
            {{ $t('Clicks') }}
          </template>

          <template #head(revenue)>
            {{ $t('Revenue') }}
          </template>

          <template #head(tax)>
            {{ $t('Tax') }}
          </template>

          <template #cell(date)="row">
            <span v-if="row.item.date">
              <strong>{{ getFormattedDate(new Date(row.item.date)) }}</strong>
            </span>
          </template>

          <template #cell(impressions)="row">
            <span v-if="row.item.impressions">
              {{ formatNumber(row.item.impressions) }}
            </span>
          </template>

          <template #cell(clicks)="row">
            <span v-if="row.item.clicks">
              {{ formatNumber(row.item.clicks) }}
            </span>
          </template>

          <template #cell(revenue)="row">
            <span v-if="row.item.revenue">
              ${{ formatNumber(row.item.revenue, 2) }}
            </span>
          </template>

          <template #cell(tax)="row">
            <span v-if="row.item.tax">
              ${{ formatNumber(row.item.tax, 2) }}
            </span>
          </template>

          <template #foot(date)>
            <strong>{{ $t('Records') }}: {{ dspPartnerStats.length }}</strong>
          </template>

          <template #foot(impressions)>
            <strong>{{ formatNumber(totalStats.impressions) }}</strong>
          </template>

          <template #foot(clicks)>
            <strong>{{ formatNumber(totalStats.clicks) }}</strong>
          </template>

          <template #foot(revenue)>
            <strong>${{ formatNumber(totalStats.revenue, 2) }}</strong>
          </template>

          <template #foot(tax)>
            <strong>${{ formatNumber(totalStats.tax, 2) }}</strong>
          </template>
        </b-table>
      </div>
      <div
        v-else
        class="alert-body text-center"
      >
        <span>{{ $t('No data for this period') }}</span>
      </div>
    </b-card>
  </div>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import {
  BCard,
  BTable,
  BSpinner,
  BButton,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import Breadcrumbs from '@/views/ui/breadcrumbs/Breadcrumbs.vue'
import PartnerToolbar from '@/views/dashboard/partners/PartnerToolbar.vue'
import getFormattedDate from '@/utils/date-formatter'
import StatsNavigation from './StatsNavigation.vue'
import StatsDateRangeFilter from './StatsDateRangeFilter.vue'

export default {
  components: {
    Breadcrumbs,
    BCard,
    BTable,
    BSpinner,
    BButton,
    PartnerToolbar,
    StatsNavigation,
    StatsDateRangeFilter,
  },
  directives: {
    Ripple,
  },
  data() {
    return {
      loading: true,
      partner: {},
      dspPartner: {},
      dspPartnerStats: [],
      breadcrumbItems: [],
      rangePicker: [],
      rangeDefault: [getFormattedDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)), getFormattedDate(new Date())],
      sortBy: 'date',
      sortDesc: true,
      sortDirection: 'desc',
      fields: [
        { key: 'date', label: 'Date', sortable: true },
        { key: 'impressions', label: 'Impressions', sortable: true },
        { key: 'clicks', label: 'Clicks', sortable: true },
        { key: 'revenue', label: 'Revenue', sortable: true },
        { key: 'tax', label: 'Tax', sortable: true },
      ],
    }
  },
  computed: {
    dspPartnerName() {
      return this.dspPartner.name || this.$route.params.dspId
    },
    totalStats() {
      if (this.dspPartnerStats.length === 0) {
        return {
          impressions: 0,
          clicks: 0,
          revenue: 0,
          tax: 0,
        }
      }

      const totals = this.dspPartnerStats.reduce(
        (acc, item) => {
          acc.impressions += item.impressions || 0
          acc.clicks += item.clicks || 0
          acc.revenue += item.revenue || 0
          acc.tax += item.tax || 0
          return acc
        },
        {
          impressions: 0,
          clicks: 0,
          revenue: 0,
          tax: 0,
        },
      )

      return totals
    },
  },
  async created() {
    this.rangePicker = this.rangeDefault
    await this.getPartnerData()
    await this.getDspPartnerData()
    await this.getDspPartnerStats()
  },
  methods: {
    setBreadcrumbs() {
      this.breadcrumbItems = [
        {
          text: this.$t('Partners'),
          to: '/partners/',
        },
        {
          text: this.partner.name,
          to: `/partners/${this.partner._id}/`,
        },
        {
          text: this.$t('Statistics'),
          to: `/partners/${this.partner._id}/stats`,
        },
        {
          text: this.$t('DSP Partners'),
          to: `/partners/${this.partner._id}/stats/dsp/partners`,
        },
        {
          text: this.dspPartnerName,
          active: true,
        },
      ]
    },
    async getPartnerData() {
      try {
        const responseData = await useJwt.getPartner(this.$route.params.id)
        this.partner = responseData.data.partner || {}
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: error.response?.statusText || 'Error',
            msg: error.message,
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    async getDspPartnerData() {
      try {
        const responseData = await useJwt.getDsppartner(this.$route.params.dspId)
        this.dspPartner = responseData.data.dsppartner || {}
        this.setBreadcrumbs()
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: error.response?.statusText || 'Error',
            msg: error.message,
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    async getDspPartnerStats() {
      this.loading = true
      try {
        let dateFrom
        let dateTo

        const selectedRange = this.rangePicker
        if (Array.isArray(selectedRange)) {
          [dateFrom, dateTo] = selectedRange
        } else {
          [dateFrom, , dateTo] = selectedRange.split(' ')
        }

        if (!dateTo && !dateFrom) {
          [dateFrom, dateTo] = this.rangeDefault
        }

        if (dateTo && dateFrom) {
          const params = {
            from: dateFrom,
            to: dateTo,
            openrtb_partner_id: this.$route.params.id,
            limit: 80,
            type: 'daily',
          }

          const responseData = await useJwt.getDsppartnerStatsRtb(this.$route.params.dspId, params)
          this.dspPartnerStats = responseData.data.items || []
        }
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: error.response?.statusText || 'Error',
            msg: error.message,
            variant: 'danger',
          },
          { root: true },
        )
        this.dspPartnerStats = []
      } finally {
        this.loading = false
      }
    },
    async toggleRange() {
      await this.getDspPartnerStats()
    },
    async refreshData() {
      await this.getDspPartnerStats()
    },
    formatNumber(value, decimals = 0) {
      if (value === null || value === undefined) return '-'
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(value)
    },
    getFormattedDate,
  },
}
</script>

<style lang="scss" scoped>
.card-statistics {
  .head-card {
    .head-card-top {
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      gap: 10px;

      h4 {
        margin: 0;
        font-weight: 600;
      }
    }

    .head-card-bottom {
      .head-card-top {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
      }
    }
  }
}

@media (max-width: 768px) {
  .card-statistics {
    .head-card {
      .head-card-top {
        flex-direction: column;
        align-items: flex-start;
      }

      .head-card-bottom {
        .head-card-top {
          justify-content: flex-start;
        }
      }
    }
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>

<style>
  .col-picker {
    min-width: 220px;
  }
</style>
